import { ENetwork } from '@NGUtils/enums';
import { isTestEnv } from '@functions/functions';
import { IWallet } from '@store/web3Store/web3Store.type';
import { NETWORKS_DATA, WALLETS } from '@store/web3Store/web3Store.variables';
import { darkTheme } from '@theme/theme';
import Onboard from '@web3-onboard/core';
import frameModule from '@web3-onboard/frame';
import injectedModule from '@web3-onboard/injected-wallets';
import walletConnectModule from '@web3-onboard/walletconnect';
import { ethers } from 'ethers';
import { metaDescriptions } from 'src/texts/seo';
import { IEVMProvider, IEVMWeb3Instance } from './evmStore.types';

async function _connectEVMWallet(newWallet: IWallet): Promise<{
	provider: IEVMProvider;
	error?: any;
}> {
	let _provider: IEVMProvider | undefined;

	switch (newWallet.name) {
		case WALLETS.blocknative.name:
			if (isTestEnv()) {
				const newProvider = new ethers.providers.JsonRpcProvider(NETWORKS_DATA.goerli.rpc);

				const newWeb3Instance = new ethers.Wallet(process.env.TEST_PRIVATE_KEY || '', newProvider);

				_provider = {
					web3Provider: undefined,
					// bad practice but used only on test env
					web3Instance: newWeb3Instance as unknown as IEVMWeb3Instance,
					isDevWallet: true
				};
			} else {
				const injected = injectedModule();
				const frame = frameModule();
				const walletConnect = walletConnectModule({
					projectId: process.env.WC_PROJECT_ID || '',
					dappUrl: 'https://ng-local.ngrok.io' /* getBaseUrl() */,
					requiredChains: [+NETWORKS_DATA[ENetwork.goerli].chainId],
					qrModalOptions: {
						themeVariables: {
							'--wcm-z-index': '999',
							'--wcm-font-family': 'Lato',
							'--wcm-font-feature-settings': 'Lato',
							'--wcm-background-color': darkTheme.colors.charcoal,
							'--wcm-accent-color': darkTheme.colors.gold,
							'--wcm-accent-fill-color': darkTheme.colors.carbon, // the Wallet Connect title
							'--wcm-container-border-radius': '5px'
						}
					}
				});

				const customTheme = {
					'--w3o-background-color': darkTheme.colors.carbon,
					'--w3o-foreground-color': darkTheme.colors.charcoal,
					'--w3o-text-color': darkTheme.colors.white,
					'--w3o-border-color': darkTheme.colors.ash,
					'--w3o-action-color': darkTheme.colors.gold,
					'--w3o-border-radius': '5px'
				};

				const appMetadata = {
					name: 'Node Guardians',
					icon: '/presskit/ng_logo_dark.svg',
					logo: '/presskit/ng_logo_light.svg',
					description: metaDescriptions.homepage,
					explore: 'https://ng-local.ngrok.io' /* getBaseUrl()*/
				};

				const onboard = Onboard({
					wallets: [injected, frame, walletConnect],
					theme: customTheme,
					appMetadata,
					connect: {
						autoConnectLastWallet: false, // /!\ need to stay false /!\
						removeWhereIsMyWalletWarning: true
					},
					accountCenter: {
						desktop: {
							enabled: false
						},
						mobile: {
							enabled: false
						}
					},
					chains: [
						{
							id: +NETWORKS_DATA[ENetwork.goerli].chainId,
							token: 'GETH',
							label: NETWORKS_DATA[ENetwork.goerli].name,
							rpcUrl: NETWORKS_DATA[ENetwork.goerli].rpc
						}
					]
				});

				const wallets = await onboard.connectWallet();

				if (!wallets[0]) {
					throw new Error('No wallet selected');
				}

				const newProvider = new ethers.providers.Web3Provider(wallets[0].provider, 'any');

				const signer = newProvider.getSigner() as IEVMWeb3Instance;

				// set address
				signer.address = wallets[0].accounts[0].address;
				// set blocknative wallet state provider
				signer.walletStateProvider = wallets[0].provider;

				_provider = {
					web3Provider: newProvider,
					web3Instance: signer
				};
			}
			break;
		default:
			break;
	}

	if (!_provider) throw new Error('No provider');

	return {
		provider: _provider,
		error: undefined
	};
}

async function switchEVMNetwork(id: number | string) {
	try {
		// convert to hex
		const hexId = typeof id === 'string' ? id : `0x${id.toString(16)}`;

		await window.ethereum.request?.({
			method: 'wallet_switchEthereumChain',
			params: [{ chainId: hexId }]
		});
	} catch (err) {
		console.error('switchEVMNetwork', err);
	}
}

export { _connectEVMWallet, switchEVMNetwork };
